import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Loader2, Maximize, Minimize, Pause, Play, X } from 'lucide-react';
import { useBoard } from '@/api/boards';
import { BoardGrid, useBoardClient } from '@/components/boards/BoardGrid';
import { ClientScopeProvider } from '@/contexts/ClientScopeContext';
import { GRID_GAP } from '@/lib/boards';
import { dashboardDisplayPath, dashboardPath } from '@/lib/routes';
import { cn } from '@/lib/utils';

/**
 * Wall display (`/dashboards/:id/display`) — rendered OUTSIDE the app shell (no sidebar/header).
 * The grid's row height is computed so the whole dashboard fits the screen; type scales with each
 * card. Controls appear on mouse movement and hide after a few seconds. Full screen uses the
 * browser Fullscreen API (F key); a screen wake lock stops the TV sleeping where supported.
 * `?cycle=<id>,<id>&every=<seconds>` rotates through several dashboards.
 * The page keeps refetching, which also keeps the Cosmos session alive.
 */
export default function DashboardDisplayPage() {
  return <ClientScopeProvider><Display /></ClientScopeProvider>;
}

const BAR = 44;
const PAD = 16;

function Display() {
  const id = useParams<{ id: string }>().id!;
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { data, isLoading, error } = useBoard(id);
  const d = data?.dashboard;
  const client = useBoardClient(d?.definition);

  // Rotation through ?cycle= dashboards.
  const cycle = useMemo(() => [id, ...(params.get('cycle') ?? '').split(',').filter((x) => /^[0-9a-f-]{36}$/i.test(x) && x !== id)], [id, params]);
  const every = Math.max(15, Math.min(3600, Number(params.get('every')) || 60));
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (cycle.length < 2 || paused) return;
    const t = setTimeout(() => {
      const next = cycle[(cycle.indexOf(id) + 1) % cycle.length];
      const rest = cycle.filter((x) => x !== next);
      navigate(dashboardDisplayPath(next, { cycle: rest, every }), { replace: true });
    }, every * 1000);
    return () => clearTimeout(t);
  }, [cycle, id, every, paused, navigate]);

  // Fit the grid to the viewport.
  const [vh, setVh] = useState(() => window.innerHeight);
  useEffect(() => { const on = () => setVh(window.innerHeight); window.addEventListener('resize', on); return () => window.removeEventListener('resize', on); }, []);
  const rows = Math.max(1, ...(d?.definition.cards ?? []).map((c) => c.y + c.h));
  const rowHeight = Math.max(36, Math.floor((vh - BAR - PAD * 2 - (rows - 1) * GRID_GAP) / rows));

  // Controls: show on mouse move, hide after 3 s.
  const [chrome, setChrome] = useState(true);
  const hide = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => {
    const show = () => { setChrome(true); clearTimeout(hide.current); hide.current = setTimeout(() => setChrome(false), 3000); };
    show();
    window.addEventListener('mousemove', show);
    return () => { window.removeEventListener('mousemove', show); clearTimeout(hide.current); };
  }, []);

  // Full screen (F key or button) + wake lock.
  const [full, setFull] = useState(!!document.fullscreenElement);
  useEffect(() => { const on = () => setFull(!!document.fullscreenElement); document.addEventListener('fullscreenchange', on); return () => document.removeEventListener('fullscreenchange', on); }, []);
  const toggleFull = () => { if (document.fullscreenElement) void document.exitFullscreen(); else void document.documentElement.requestFullscreen().catch(() => {}); };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === 'f' && !e.ctrlKey && !e.metaKey) toggleFull();
      if (e.key === ' ' && cycle.length > 1) { e.preventDefault(); setPaused((p) => !p); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [cycle.length]);
  useEffect(() => {
    let lock: { release: () => Promise<void> } | null = null;
    const nav = navigator as Navigator & { wakeLock?: { request: (t: 'screen') => Promise<{ release: () => Promise<void> }> } };
    const get = () => nav.wakeLock?.request('screen').then((l) => { lock = l; }).catch(() => {});
    void get();
    const vis = () => { if (document.visibilityState === 'visible') void get(); };
    document.addEventListener('visibilitychange', vis);
    return () => { document.removeEventListener('visibilitychange', vis); void lock?.release().catch(() => {}); };
  }, []);

  useEffect(() => {
    if (!d) return;
    const before = document.title;
    document.title = `${d.name} · Cosmos`;
    return () => { document.title = before; };
  }, [d?.name]);

  return (
    <div className={cn('h-screen overflow-hidden bg-background text-foreground', !chrome && 'cursor-none')}>
      <header className="flex items-center gap-3 px-4" style={{ height: BAR }}>
        <div className="min-w-0 flex-1 truncate text-[clamp(0.95rem,1.4vw,1.5rem)] font-semibold">
          {d?.name ?? 'Dashboard'}
          {client.name && <span className="ml-3 font-normal text-muted-foreground">{client.name}</span>}
        </div>
        {cycle.length > 1 && <span className="text-xs tabular-nums text-muted-foreground">{cycle.indexOf(id) + 1} / {cycle.length}{paused && ' · paused'}</span>}
        <div className={cn('flex items-center gap-1 transition-opacity', chrome ? 'opacity-100' : 'pointer-events-none opacity-0')}>
          {cycle.length > 1 && <IconBtn label={paused ? 'Resume rotation (space)' : 'Pause rotation (space)'} onClick={() => setPaused((p) => !p)}>{paused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}</IconBtn>}
          <IconBtn label={full ? 'Exit full screen (F)' : 'Full screen (F)'} onClick={toggleFull}>{full ? <Minimize className="h-4 w-4" /> : <Maximize className="h-4 w-4" />}</IconBtn>
          <Link to={dashboardPath(id)} onClick={() => { if (document.fullscreenElement) void document.exitFullscreen(); }} title="Leave the display"
            className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"><X className="h-4 w-4" /></Link>
        </div>
      </header>
      <main style={{ padding: `0 ${PAD}px ${PAD}px` }}>
        {isLoading ? <div className="flex items-center gap-2 py-24 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /> Loading…</div>
          : error || !d ? <div className="py-24 text-destructive">{error instanceof Error ? error.message : 'Dashboard not found'}</div>
          : d.definition.cards.length === 0 ? <div className="py-24 text-muted-foreground">This dashboard has no cards.</div>
          : <BoardGrid def={d.definition} display rowHeight={rowHeight} />}
      </main>
    </div>
  );
}

function IconBtn({ label, onClick, children }: { label: string; onClick: () => void; children: React.ReactNode }) {
  return <button type="button" onClick={onClick} title={label} aria-label={label} className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground">{children}</button>;
}
